// Query keys as factories, with the invalidation map beside them as data
// (Build Book §11a).
//
// A static check bans inline key literals in a feature folder. Nine call sites
// spelling ['products'] and a tenth spelling ['product'] self-heals on window
// focus and gets filed forever as "a bit laggy".
//
// Every filter the request sends is part of the key — otherwise the 31–60
// ageing bucket serves Current's rows and a cashier chases the wrong customer.

export const keys = {
  me: () => ['me'],
  tenant: () => ['tenant'],
  // The nightly reconciliation's last few nights, on Settings. Under `tenant`
  // because it is part of the shop's own record rather than a resource of its
  // own — and deliberately a level below it, so saving the GSTIN replaces
  // ['tenant'] without disturbing a panel that has nothing to do with it.
  jobRuns: (filters = {}) => ['tenant', 'jobs', filters],

  products: (filters = {}) => ['products', filters],
  product: (id) => ['products', 'detail', id],
  productSearch: (q) => ['products', 'search', q],
  // The product form's live packaging sentence. Keyed by the five factors it
  // is computed from, because that is exactly what the request sends — and the
  // answer to one set of factors never changes, so it caches forever.
  productConversion: (factors = {}) => ['products', 'conversion', factors],
  // Why a tile's stock moved when it was neither bought nor sold. NOT under
  // ['products'], deliberately: an adjustment invalidates the product it moved
  // and this list, and nesting it would mean every price edit on any tile threw
  // away a history that has nothing to do with prices.
  stockAdjustments: (filters = {}) => ['stock-adjustments', filters],

  // Price list imports (§11). The batch detail is keyed by its filters too,
  // because a 400-row preview pages and an outcome filter changes the rows.
  imports: (filters = {}) => ['imports', filters],
  importBatch: (id, filters = {}) => ['imports', 'detail', id, filters],

  // The three first-day files (V015): products with opening stock, suppliers,
  // customers. Their own namespace rather than under ['imports'], so a price
  // list commit does not throw away the customer import's history, and the
  // other way round.
  // For products, one product type's own template (V017); '' is the generic one.
  bulkImportSpec: (kind, productTypeId = '') => ['bulk-imports', 'spec', kind, productTypeId],
  bulkImports: (kind, filters = {}) => ['bulk-imports', 'history', kind, filters],

  customers: (filters = {}) => ['customers', filters],
  customer: (id) => ['customers', 'detail', id],
  customerLedger: (id, filters = {}) => ['customers', 'ledger', id, filters],

  suppliers: (filters = {}) => ['suppliers', filters],
  supplier: (id) => ['suppliers', 'detail', id],
  supplierLedger: (id, filters = {}) => ['suppliers', 'ledger', id, filters],

  sales: (filters = {}) => ['sales', filters],
  sale: (id) => ['sales', 'detail', id],

  // Money in, its own namespace rather than a `view:` discriminator under
  // sales. A receipt outlives the bill it was taken against — an on-account one
  // has no bill at all — and the Receive-payment screen reads it with nothing
  // from /api/sales on the page.
  receipts: (filters = {}) => ['receipts', filters],
  receipt: (id) => ['receipts', 'detail', id],

  purchases: (filters = {}) => ['purchases', filters],
  purchase: (id) => ['purchases', 'detail', id],

  // A want, not a document (V010) — its own namespace rather than a `view:`
  // discriminator under purchases, the same call receipts made against sales:
  // a request outlives no purchase and starts belonging to none.
  productRequests: (filters = {}) => ['product-requests', filters],
  productRequest: (id) => ['product-requests', 'detail', id],

  // What it cost to run the shop (V020). Its own namespace: an expense is not
  // a purchase and belongs to no supplier, and the list carries its own
  // period totals, so nothing else's key would describe it.
  expenses: (filters = {}) => ['expenses', filters],

  outstanding: (filters = {}) => ['outstanding', filters],
  // The customer views behind To collect / Advances held / Brought forward.
  // Under ['outstanding'], so a receipt that invalidates the bills list drops
  // these too.
  outstandingCustomers: (filters = {}) => ['outstanding', 'customers', filters],
  // The payable twin of `outstanding` above — its own key, not a filter on
  // the same one, because the two come from different endpoints
  // (outstanding-ageing vs outstanding-payable) and invalidating one must
  // never be read as having refreshed the other.
  outstandingPayable: () => ['reports', 'outstanding-payable'],
  dayBook: (filters = {}) => ['reports', 'day-book', filters],
  stockOnHand: (filters = {}) => ['reports', 'stock-on-hand', filters],
  gstSummary: (filters = {}) => ['reports', 'gst-summary', filters],
  // The owner's charts. Under ['reports'] like every read that sums the
  // documents, keyed by the chart grain because day and month are different
  // answers from the same endpoint.
  insights: (grain) => ['reports', 'insights', grain],
  // The owner's check of one day's money (V021), keyed by the day.
  dayCheck: (day) => ['reports', 'day-check', day ?? 'today'],

  activity: (filters = {}) => ['activity', filters],
  config: (type) => ['config', type],
  // V017. The product types, categories and per-type lists, read whole. The
  // SKU preview sits under it so a catalog edit (a brand's code) drops every
  // preview built from the old one.
  catalog: () => ['catalog'],
  skuPreview: (params = {}) => ['catalog', 'sku-preview', params],
  skuRebuild: () => ['catalog', 'sku-rebuild'],

  // Shop-declared fields (V008). Keyed by entity because the product form and
  // the customer form each read only their own, and a shared key would make
  // adding a customer field refetch every product screen in the app.
  customFields: (entity) => ['custom-fields', entity ?? 'all'],
}

// Invalidation, decided once. Editing a product invalidates products only —
// never sales, because Rule 5 means a posted document does not change when its
// product does.
export const invalidates = {
  postSale: (customerId) => [
    ['products'],
    ['sales'],
    ['outstanding'],
    ['reports', 'day-book'],
    ['reports', 'insights'],
    ['customers', 'detail', customerId],
    ['customers', 'ledger', customerId],
  ],
  postPurchase: (supplierId) => [
    ['products'],
    ['purchases'],
    ['reports', 'day-book'],
    ['reports', 'insights'],
    // Every route that changes what a shop owes a supplier — a new bill, a
    // payment against one, an on-account advance — shares this one bucket
    // (see purchases/queries.js), so the payable dashboard tile is covered
    // here rather than needing its own invalidation call at each of the
    // three call sites.
    ['reports', 'outstanding-payable'],
    ['suppliers', 'detail', supplierId],
    ['suppliers', 'ledger', supplierId],
    // A bill can close open requests (V010) — invalidated unconditionally
    // rather than only when the response actually carried a fulfilled one,
    // because a request the shop is not currently viewing is exactly the
    // stale-list case this whole map exists to prevent.
    ['product-requests'],
  ],
  // `saleId` is null for an on-account receipt, which is money in against no
  // bill at all — so the sale key is dropped rather than sent as
  // ['sales','detail',null], which matches nothing and reads as an oversight.
  //
  // ['receipts'] and ['customers'] are both here since V006. The first because
  // an advance changes what the Receive-payment screen may spend, and the
  // second because the customer LIST carries `unallocated_credit` now, not only
  // the detail page — a screen showing yesterday's credit beside today's
  // balance is the disagreement this whole change exists to end.
  takeReceipt: (saleId, customerId) => [
    ...(saleId ? [['sales', 'detail', saleId]] : []),
    ['receipts'],
    ['outstanding'],
    ['reports', 'day-book'],
    ['reports', 'insights'],
    ['customers'],
    ['customers', 'detail', customerId],
    ['customers', 'ledger', customerId],
  ],
  // A same-day cancel touches everything posting the bill touched, in reverse:
  // the stock went back on the shelf, the bill left the day book and the
  // customer's ledger, and the outstanding list is one row shorter. So it
  // invalidates the same set — plus the document itself, which postSale does not
  // need to name because it did not exist a moment ago and this one is on screen.
  cancelSale: (saleId, customerId) => [
    ...invalidates.postSale(customerId),
    ['sales', 'detail', saleId],
  ],
  cancelPurchase: (purchaseId, supplierId) => [
    ...invalidates.postPurchase(supplierId),
    ['purchases', 'detail', purchaseId],
  ],
  // Stock moved, so every screen that shows a stock figure is now wrong: the
  // product, the list it sits in, the low-stock tile Today counts off it, and
  // the valuation report. Never sales or purchases — Rule 5 means a posted
  // document does not change when the product it names does.
  adjustStock: () => [
    ['products'],
    ['stock-adjustments'],
    ['reports', 'stock-on-hand'],
  ],
  // A brand or finish typed on the product form joins the shop's list on the
  // server, so the catalog is re-read with the products.
  editProduct: () => [['products'], ['catalog']],
  editCatalog: () => [['catalog'], ['products']],
  // A deleted product never moved, so no document or history names it — but
  // its opening stock leaves the stock report with it.
  deleteProduct: () => [['products'], ['reports', 'stock-on-hand']],
  editParty: () => [['customers'], ['suppliers']],
  // A bulk import creates masters and, for an advance, posts a receipt or a
  // supplier payment — so it touches what creating those one at a time would.
  // Products bring stock with them, so the stock report moves too.
  bulkImport: (kind) => {
    const common = [['bulk-imports', 'history', kind], ['reports', 'day-book']]
    if (kind === 'products') return [...common, ['products'], ['reports', 'stock-on-hand']]
    if (kind === 'suppliers') {
      // Supplier payments are listed under ['purchases'] (purchases/queries.js).
      return [...common, ['suppliers'], ['purchases'], ['reports', 'outstanding-payable']]
    }
    return [...common, ['customers'], ['receipts'], ['outstanding']]
  },
  // Creating or cancelling a want moves no stock and touches nothing else —
  // its own list is the whole blast radius.
  productRequest: () => [['product-requests']],
  // An expense moves money out and nothing else: its own list, and the
  // charts that sum it beside sales and purchases.
  recordExpense: () => [['expenses'], ['reports', 'insights'], ['reports', 'day-check']],
  checkDay: () => [['reports', 'day-check']],
}

/** Invalidate after commit, never inside the transaction — a concurrent read
 *  would repopulate stale data for the full TTL. */
export function applyInvalidation(queryClient, targets) {
  for (const key of targets) {
    queryClient.invalidateQueries({ queryKey: key })
  }
}
